import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Globe, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { api, errMsg } from "../lib/api";
import { TournamentCard } from "../components/TournamentCard";

const Header = ({ kicker, title, children }) => (
  <section className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
    <div>
      <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-white mb-2" data-testid="back-to-dashboard"><ArrowLeft size={14} /> Dashboard</Link>
      <p className="font-mono text-xs uppercase tracking-widest text-emerald-400">{kicker}</p>
      <h1 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tight">{title}</h1>
    </div>
    {children}
  </section>
);

export function MyTournamentsPage() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/tournaments").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  return (
    <div className="space-y-6">
      <Header kicker="Le tue leghe" title="I miei tornei" />
      {list === null && <div className="card-surface h-28 animate-pulse" />}
      {list?.length === 0 && <p className="card-surface p-6 text-slate-300" data-testid="no-tournaments">Non sei in nessun torneo. Creane uno dalla dashboard, entra con un codice invito o partecipa a un torneo pubblico.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">{list?.map((t, i) => <TournamentCard key={t.id} t={t} index={i} />)}</div>
    </div>
  );
}

const PublicCard = ({ t, onJoin }) => (
  <div className="card-surface p-4 sm:p-6 flex flex-col gap-3" data-testid={`public-tournament-${t.id}`}>
    <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">{t.competition_name}</span>
    <h3 className="font-display text-xl sm:text-2xl font-bold uppercase truncate">{t.name}</h3>
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-1.5 text-sm text-slate-400"><Users size={14} /> {t.members_count}</span>
      {t.is_member ? (
        <Link to={`/tournament/${t.id}`} data-testid={`public-open-${t.id}`} className="h-10 px-5 inline-flex items-center rounded-full border border-emerald-500/60 text-emerald-400 text-sm font-semibold">Apri</Link>
      ) : (
        <Button onClick={() => onJoin(t)} data-testid={`public-join-${t.id}`} className="h-10 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0D14] font-bold">Entra</Button>
      )}
    </div>
  </div>
);

export function PublicTournamentsPage() {
  const [list, setList] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/tournaments/public").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  const join = async (t) => {
    try {
      await api.post(`/tournaments/${t.id}/join-public`);
      toast.success(`Sei entrato in ${t.name}`);
      nav(`/tournament/${t.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6">
      <Header kicker="Aperti a tutti" title="Tornei pubblici" />
      <p className="text-sm text-slate-400 flex items-start gap-2"><Globe size={16} className="shrink-0 mt-0.5 text-emerald-400" /> Chiunque può entrare. Se entri a torneo iniziato parti con i punti dell'ultimo in classifica.</p>
      {list === null && <div className="card-surface h-28 animate-pulse" />}
      {list?.length === 0 && <p className="card-surface p-6 text-slate-300" data-testid="no-public-tournaments">Nessun nuovo torneo pubblico disponibile. Quelli a cui partecipi sono in "I miei tornei".</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">{list?.map((t) => <PublicCard key={t.id} t={t} onJoin={join} />)}</div>
    </div>
  );
}
